import type { DashboardLayoutItemV4 } from "../../redux/widgets.types"
import { HOME_GRID_COLUMNS, type HomeLayoutItem } from "./homeLayout"

/**
 * Home's editable page: an ordered list of SECTIONS, each OWNING its widgets.
 *
 * The previous model put section headings on the same grid as the widgets,
 * as items of their own. A heading could then be dragged away from the
 * widgets it introduced, and a section's membership was only inferred from
 * which heading happened to sit above a widget. Here the relationship is
 * explicit: every widget names its section, its position is relative to that
 * section's own grid, and a section moves as one block — its heading and all
 * its widgets together.
 *
 * A widget is one unit (header, actions, content, optional footer), placed by
 * one geometry entry. Renaming a widget or a section changes its `label` and
 * nothing else.
 *
 * Geometry is in rows of `HOME_ROW_HEIGHT` px with a 16px gutter, so a row
 * step is 24px and an item `h` rows tall is `24h - 16` px.
 */

export const HOME_ROW_HEIGHT = 8
export const HOME_GUTTER = 16

export const HOME_PAGE_SECTIONS = [
  "overview",
  "financial",
  "schedule",
  "pipeline"
] as const

export type HomePageSectionId = (typeof HOME_PAGE_SECTIONS)[number]

/**
 * The overview — priority actions and Locations — carries no heading: each of
 * its widgets says what it is in its own header, and a heading above them
 * would only repeat that.
 */
export const SECTION_HAS_HEADING: Record<HomePageSectionId, boolean> = {
  overview: false,
  financial: true,
  schedule: true,
  pipeline: true
}

export const isPageSection = (id: string): id is HomePageSectionId =>
  (HOME_PAGE_SECTIONS as readonly string[]).includes(id)

export interface PageSection {
  id: HomePageSectionId
  /** A user's own name for the section; absent means the standard one. */
  label?: string
}

export interface PlacedWidget extends HomeLayoutItem {
  section: HomePageSectionId
  label?: string
}

export interface HomePage {
  sections: PageSection[]
  widgets: PlacedWidget[]
}

/** One placeable widget and the size it opens at. */
export interface HomeUnitSpec {
  id: string
  section: HomePageSectionId
  w: number
  h: number
  minW: number
  minH: number
  /** As tall as its content, measured on the page; only width is offered. */
  autoHeight?: boolean
}

export const rowsToPx = (h: number): number =>
  h * HOME_ROW_HEIGHT + (h - 1) * HOME_GUTTER

/** The fewest rows that hold `px` of content. */
export const pxToRows = (px: number): number =>
  Math.max(1, Math.ceil((px + HOME_GUTTER) / (HOME_ROW_HEIGHT + HOME_GUTTER)))

// ── Grid arithmetic inside one section ─────────────────────────────────────

const overlaps = (a: HomeLayoutItem, b: HomeLayoutItem): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h

const bottomOf = (items: readonly HomeLayoutItem[]): number =>
  items.reduce((acc, item) => Math.max(acc, item.y + item.h), 0)

const byReadingOrder = (a: HomeLayoutItem, b: HomeLayoutItem): number =>
  a.y - b.y || a.x - b.x

/**
 * Vertical compaction with collision resolution: in reading order, each item
 * rises until something stops it, then drops below anything it overlaps.
 * This is what closes the space a moved or removed widget leaves.
 */
export const compactItems = <T extends HomeLayoutItem>(
  items: readonly T[]
): T[] => {
  const placed: T[] = []
  for (const original of [...items].sort(byReadingOrder)) {
    const item = { ...original }
    item.w = Math.min(item.w, HOME_GRID_COLUMNS)
    item.x = Math.max(0, Math.min(item.x, HOME_GRID_COLUMNS - item.w))
    item.y = Math.max(0, Math.min(item.y, bottomOf(placed)))
    while (
      item.y > 0 &&
      !placed.some((other) => overlaps({ ...item, y: item.y - 1 }, other))
    ) {
      item.y -= 1
    }
    let collider = placed.find((other) => overlaps(item, other))
    while (collider) {
      item.y = collider.y + collider.h
      collider = placed.find((other) => overlaps(item, other))
    }
    placed.push(item)
  }
  return placed
}

/** Holds `id` and pushes what it covers down, each push cascading. */
const pushDownFrom = <T extends HomeLayoutItem>(
  items: readonly T[],
  id: string
): T[] => {
  const self = items.find((item) => item.i === id)
  if (!self) return [...items]
  const placed: T[] = [self]
  for (const original of [...items].sort(byReadingOrder)) {
    if (original.i === id) continue
    const item = { ...original }
    let collider = placed.find((other) => overlaps(item, other))
    while (collider) {
      item.y = collider.y + collider.h
      collider = placed.find((other) => overlaps(item, other))
    }
    placed.push(item)
  }
  return placed
}

/**
 * A widget the user did not touch but that was pushed onto a new row slides
 * to the leftmost free place on it, so a push never leaves half a row empty.
 */
const settleDisplaced = <T extends HomeLayoutItem>(
  before: readonly T[],
  after: readonly T[],
  actorId: string
): T[] => {
  const was = new Map(before.map((item) => [item.i, item]))
  let next = compactItems(after)
  for (const item of [...next].sort(byReadingOrder)) {
    if (item.i === actorId) continue
    const old = was.get(item.i)
    if (!old || old.y === item.y || item.x === 0) continue
    const others = next.filter((other) => other.i !== item.i)
    for (let x = 0; x < item.x; x += 1) {
      const candidate = { ...item, x }
      if (!others.some((other) => overlaps(candidate, other))) {
        next = next.map((other) => (other.i === item.i ? candidate : other))
        break
      }
    }
  }
  return compactItems(next)
}

const freeSlotIn = (
  items: readonly HomeLayoutItem[],
  w: number,
  h: number
): { x: number; y: number } => {
  const bottom = bottomOf(items)
  for (let y = 0; y <= bottom; y += 1) {
    for (let x = 0; x + w <= HOME_GRID_COLUMNS; x += 1) {
      const candidate = { i: "?", x, y, w, h }
      if (!items.some((item) => overlaps(candidate, item))) return { x, y }
    }
  }
  return { x: 0, y: bottom }
}

// ── The page ───────────────────────────────────────────────────────────────

export const widgetsIn = (
  page: HomePage,
  section: HomePageSectionId
): PlacedWidget[] => page.widgets.filter((widget) => widget.section === section)

/** Replaces one section's widgets, leaving every other section untouched. */
const withSection = (
  page: HomePage,
  section: HomePageSectionId,
  widgets: PlacedWidget[]
): HomePage => ({
  sections: page.sections,
  widgets: [
    ...page.widgets.filter((widget) => widget.section !== section),
    ...widgets.map((widget) => ({ ...widget, section }))
  ]
})

/** Compacts every section on its own. */
export const normalizePage = (page: HomePage): HomePage => ({
  sections: page.sections,
  widgets: page.sections.flatMap((section) =>
    compactItems(widgetsIn(page, section.id))
  )
})

const appendTo = (
  page: HomePage,
  section: HomePageSectionId,
  widget: Omit<PlacedWidget, "x" | "y" | "section">
): HomePage => {
  const existing = widgetsIn(page, section)
  const slot = freeSlotIn(existing, widget.w, widget.h)
  return withSection(page, section, [
    ...existing,
    { ...widget, section, x: slot.x, y: slot.y }
  ])
}

/** The page My View opens with: every widget in its section, in order. */
export const defaultPage = (units: readonly HomeUnitSpec[]): HomePage => {
  let page: HomePage = {
    sections: HOME_PAGE_SECTIONS.map((id) => ({ id })),
    widgets: []
  }
  for (const unit of units) {
    page = appendTo(page, unit.section, {
      i: unit.id,
      w: unit.w,
      h: unit.h
    })
  }
  return normalizePage(page)
}

/**
 * Brings a stored page in line with what this user can see now: widgets no
 * longer shown drop out, newly shown ones join the end of their section,
 * sizes below a widget's minimum are lifted to it, a missing section is
 * appended. What the page RENDERS — nothing here is written back.
 */
export const reconcilePage = (
  stored: HomePage | null,
  units: readonly HomeUnitSpec[]
): HomePage => {
  if (!stored || stored.sections.length === 0) return defaultPage(units)
  const shown = new Map(units.map((unit) => [unit.id, unit]))
  const sections = [
    ...stored.sections.filter((section) => isPageSection(section.id)),
    ...HOME_PAGE_SECTIONS.filter(
      (id) => !stored.sections.some((section) => section.id === id)
    ).map((id) => ({ id }))
  ]
  let page: HomePage = {
    sections,
    widgets: stored.widgets
      .filter((widget) => shown.has(widget.i) && isPageSection(widget.section))
      .map((widget) => {
        const unit = shown.get(widget.i)!
        return {
          ...widget,
          w: Math.min(HOME_GRID_COLUMNS, Math.max(widget.w, unit.minW)),
          h: Math.max(widget.h, unit.minH)
        }
      })
  }
  const placed = new Set(page.widgets.map((widget) => widget.i))
  for (const unit of units) {
    if (placed.has(unit.id)) continue
    page = appendTo(page, unit.section, { i: unit.id, w: unit.w, h: unit.h })
  }
  return normalizePage(page)
}

/** Sections that hold at least one widget — what the page draws. */
export const visibleSections = (page: HomePage): PageSection[] =>
  page.sections.filter((section) => widgetsIn(page, section.id).length > 0)

export const sectionOf = (
  page: HomePage,
  id: string
): HomePageSectionId | null =>
  page.widgets.find((widget) => widget.i === id)?.section ?? null

/** Moves a whole section — its heading and all its widgets — to `toIndex`. */
export const moveSection = (
  page: HomePage,
  section: HomePageSectionId,
  toIndex: number
): HomePage => {
  const from = page.sections.findIndex((candidate) => candidate.id === section)
  if (from === -1) return page
  const target = Math.max(0, Math.min(page.sections.length - 1, toIndex))
  if (target === from) return page
  const sections = [...page.sections]
  const [moved] = sections.splice(from, 1)
  sections.splice(target, 0, moved)
  return { sections, widgets: page.widgets }
}

/**
 * Moves a whole widget into another section. With `at`, it lands there and
 * pushes what it covers down (a pointer drop); without, it joins the end.
 * Its old section closes up behind it.
 */
export const moveToSection = (
  page: HomePage,
  id: string,
  section: HomePageSectionId,
  at?: { x: number; y: number }
): HomePage => {
  const widget = page.widgets.find((candidate) => candidate.i === id)
  if (!widget) return page
  const without: HomePage = {
    sections: page.sections,
    widgets: page.widgets.filter((candidate) => candidate.i !== id)
  }
  if (!at) {
    return normalizePage(
      appendTo(without, section, {
        i: id,
        w: widget.w,
        h: widget.h,
        label: widget.label
      })
    )
  }
  const placed: PlacedWidget = {
    ...widget,
    section,
    x: Math.max(0, Math.min(at.x, HOME_GRID_COLUMNS - widget.w)),
    y: Math.max(0, at.y)
  }
  const target = [...widgetsIn(without, section), placed]
  return normalizePage(
    withSection(
      without,
      section,
      settleDisplaced(target, pushDownFrom(target, id), id)
    )
  )
}

/** Drops a widget; its section closes up behind it. */
export const removeWidget = (page: HomePage, id: string): HomePage =>
  normalizePage({
    sections: page.sections,
    widgets: page.widgets.filter((widget) => widget.i !== id)
  })

const editInSection = (
  page: HomePage,
  id: string,
  edit: (items: PlacedWidget[]) => PlacedWidget[]
): HomePage => {
  const section = sectionOf(page, id)
  if (!section) return page
  const before = widgetsIn(page, section)
  return withSection(page, section, settleDisplaced(before, edit(before), id))
}

/** Sets a widget's width, floored at its minimum and kept on the canvas. */
export const setWidth = (
  page: HomePage,
  id: string,
  w: number,
  minW: number
): HomePage => {
  const width = Math.min(HOME_GRID_COLUMNS, Math.max(w, minW))
  return editInSection(page, id, (items) =>
    pushDownFrom(
      items.map((item) =>
        item.i === id
          ? {
              ...item,
              w: width,
              x: Math.min(item.x, HOME_GRID_COLUMNS - width)
            }
          : item
      ),
      id
    )
  )
}

/** Grows or shrinks a widget by `delta` rows, never below its minimum. */
export const resizeHeight = (
  page: HomePage,
  id: string,
  delta: number,
  minH: number
): HomePage =>
  editInSection(page, id, (items) =>
    pushDownFrom(
      items.map((item) =>
        item.i === id ? { ...item, h: Math.max(minH, item.h + delta) } : item
      ),
      id
    )
  )

/** One column step left or right, for a widget narrower than the canvas. */
export const nudgeHorizontally = (
  page: HomePage,
  id: string,
  direction: "left" | "right"
): HomePage =>
  editInSection(page, id, (items) => {
    const self = items.find((item) => item.i === id)
    if (!self) return items
    const x =
      direction === "left"
        ? Math.max(0, self.x - 1)
        : Math.min(HOME_GRID_COLUMNS - self.w, self.x + 1)
    return pushDownFrom(
      items.map((item) => (item.i === id ? { ...item, x } : item)),
      id
    )
  })

/**
 * The keyboard's Move up / Move down: swaps with the neighbour in reading
 * order, and at a section's edge crosses into the next visible section.
 */
export const moveInReadingOrder = (
  page: HomePage,
  id: string,
  direction: "earlier" | "later"
): HomePage => {
  const section = sectionOf(page, id)
  if (!section) return page
  const items = widgetsIn(page, section).sort(byReadingOrder)
  const index = items.findIndex((item) => item.i === id)
  const neighbour = items[direction === "earlier" ? index - 1 : index + 1]
  if (neighbour) {
    const self = items[index]
    return withSection(
      page,
      section,
      compactItems(
        items.map((item) => {
          if (item.i === self.i)
            return { ...item, x: neighbour.x, y: neighbour.y }
          if (item.i === neighbour.i) return { ...item, x: self.x, y: self.y }
          return item
        })
      )
    )
  }
  const order = page.sections.map((candidate) => candidate.id)
  const step = direction === "earlier" ? -1 : 1
  for (
    let at = order.indexOf(section) + step;
    at >= 0 && at < order.length;
    at += step
  ) {
    const next = order[at]
    if (direction === "earlier") return moveToSection(page, id, next)
    return moveToSection(page, id, next, { x: 0, y: 0 })
  }
  return page
}

/**
 * Applies a finished pointer drag or resize inside one section. A drop onto
 * a widget of the same size swaps the two — "put this one before that one";
 * anything else keeps the grid's own arrangement, with pushed widgets
 * settled left.
 */
export const applyGridChange = (
  page: HomePage,
  section: HomePageSectionId,
  next: readonly HomeLayoutItem[],
  actorId: string,
  kind: "drag" | "resize"
): HomePage => {
  const before = widgetsIn(page, section)
  const labels = new Map(before.map((item) => [item.i, item.label]))
  const moved = next.find((item) => item.i === actorId)
  if (kind === "drag" && moved) {
    const self = before.find((item) => item.i === actorId)
    const covered = before.filter(
      (item) =>
        item.i !== actorId &&
        overlaps(
          { ...moved, w: self?.w ?? moved.w, h: self?.h ?? moved.h },
          item
        )
    )
    const other = covered.length === 1 ? covered[0] : null
    if (
      self &&
      other &&
      other.w === self.w &&
      other.h === self.h &&
      Math.abs(other.x - moved.x) * 2 < other.w &&
      Math.abs(other.y - moved.y) * 2 < other.h
    ) {
      return withSection(
        page,
        section,
        compactItems(
          before.map((item) => {
            if (item.i === self.i) return { ...item, x: other.x, y: other.y }
            if (item.i === other.i) return { ...item, x: self.x, y: self.y }
            return item
          })
        )
      )
    }
  }
  const after: PlacedWidget[] = next
    .filter((item) => labels.has(item.i))
    .map(({ i, x, y, w, h }) => ({
      i,
      x,
      y,
      w,
      h,
      section,
      ...(labels.get(i) ? { label: labels.get(i) } : {})
    }))
  return withSection(page, section, settleDisplaced(before, after, actorId))
}

/** Applies measured content heights (rows) to the widgets that size to them. */
export const withMeasuredHeights = (
  page: HomePage,
  heights: Readonly<Record<string, number>>
): HomePage => {
  let changed = false
  const widgets = page.widgets.map((widget) => {
    const h = heights[widget.i]
    if (!h || h === widget.h) return widget
    changed = true
    return { ...widget, h }
  })
  return changed ? normalizePage({ sections: page.sections, widgets }) : page
}

export const renameWidget = (
  page: HomePage,
  id: string,
  label: string
): HomePage => ({
  sections: page.sections,
  widgets: page.widgets.map((widget) =>
    widget.i === id ? withLabel(widget, label) : widget
  )
})

export const renameSection = (
  page: HomePage,
  id: HomePageSectionId,
  label: string
): HomePage => ({
  sections: page.sections.map((section) =>
    section.id === id ? withLabel(section, label) : section
  ),
  widgets: page.widgets
})

const withLabel = <T extends { label?: string }>(item: T, label: string): T => {
  const trimmed = label.trim()
  const { label: _old, ...rest } = item
  return (trimmed ? { ...rest, label: trimmed } : rest) as T
}

// ── Storage ────────────────────────────────────────────────────────────────

const SECTION_PREFIX = "section:"

/**
 * The page as the view's `layout` array, which `ezbuild_back` stores
 * verbatim. Sections are entries carrying only their order (`y`) and label;
 * every widget names its section in `s`, with geometry relative to it.
 */
export const pageToStored = (page: HomePage): DashboardLayoutItemV4[] => [
  ...page.sections.map((section, index) => ({
    i: `${SECTION_PREFIX}${section.id}`,
    x: 0,
    y: index,
    w: HOME_GRID_COLUMNS,
    h: 0,
    ...(section.label ? { label: section.label } : {})
  })),
  ...page.widgets.map((widget) => ({
    i: widget.i,
    x: widget.x,
    y: widget.y,
    w: widget.w,
    h: widget.h,
    s: widget.section,
    ...(widget.label ? { label: widget.label } : {})
  }))
]

/**
 * The first editable layout (one grid whose headings were grid items) is read
 * by position: a widget belongs to the nearest heading above it. Its two
 * heading-only sections merge into the overview, its single financial band
 * becomes the three metric widgets at the same place, and the two strips
 * that are no longer placeable (the counts, the report links) are dropped.
 */
const LEGACY_SECTION: Record<string, HomePageSectionId> = {
  today: "overview",
  locations: "overview",
  financial: "financial",
  schedule: "schedule",
  pipeline: "pipeline"
}

const readLegacyFlat = (items: readonly DashboardLayoutItemV4[]): HomePage => {
  const sorted = [...items].sort(byReadingOrder)
  const sections: PageSection[] = []
  const widgets: PlacedWidget[] = []
  let current: HomePageSectionId = "overview"
  let top = 0
  // Two old sections can merge into one; the second continues below the first.
  let offset = 0
  for (const item of sorted) {
    if (item.i.startsWith(SECTION_PREFIX)) {
      current = LEGACY_SECTION[item.i.slice(SECTION_PREFIX.length)] ?? current
      top = item.y + item.h
      offset = bottomOf(widgets.filter((widget) => widget.section === current))
      if (!sections.some((section) => section.id === current))
        sections.push({ id: current })
      continue
    }
    if (item.i === "home:today_counts" || item.i === "home:favorite_reports")
      continue
    const y = Math.max(0, item.y - top) + offset
    if (item.i === "home:financial_metrics") {
      ;["home:revenue", "home:receivables", "home:overdue"].forEach((i, n) =>
        widgets.push({ i, x: n * 4, y, w: 4, h: 5, section: current })
      )
      continue
    }
    widgets.push({
      i: item.i,
      x: item.x,
      y,
      w: item.w,
      h: item.h,
      section: current
    })
  }
  return normalizePage({ sections, widgets })
}

/** Reads a stored layout back; `null` when there is none to read. */
export const pageFromStored = (
  items: readonly DashboardLayoutItemV4[] | undefined | null
): HomePage | null => {
  if (!items || items.length === 0) return null
  const sectionItems = items.filter((item) => item.i.startsWith(SECTION_PREFIX))
  const widgetItems = items.filter((item) => !item.i.startsWith(SECTION_PREFIX))
  const owned = widgetItems.every((item) => typeof item.s === "string")
  if (!owned || sectionItems.some((item) => item.h > 0)) {
    return readLegacyFlat(items)
  }
  const sections: PageSection[] = [...sectionItems]
    .sort((a, b) => a.y - b.y)
    .map((item) => ({
      id: item.i.slice(SECTION_PREFIX.length) as HomePageSectionId,
      ...(item.label ? { label: item.label } : {})
    }))
    .filter((section) => isPageSection(section.id))
  const widgets: PlacedWidget[] = widgetItems
    .filter((item) => isPageSection(item.s ?? ""))
    .map((item) => ({
      i: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      section: item.s as HomePageSectionId,
      ...(item.label ? { label: item.label } : {})
    }))
  return { sections, widgets }
}
